import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Divider,
  Image,
  Loader,
  NumberInput
} from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { GripVertical, Plus, Trash } from 'tabler-icons-react';
import AppFlexbox from '../../common/AppFlexbox';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import SortableList from '../../common/SortableList';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import { getEcomStoreProductStatus } from '../../../helpers/vendorHelper';
import AppCard from '../../common/AppCard';
import { useModalState } from '../../../helpers/hooks';
import EditProductCard from '../products/EditProductCard';
import ActionableIcon from '../../common/ActionableIcon';
import StoreProductAssignModal from './StoreProductAssignModal';
import { currencyFormat } from '../../../helpers/format';

const StoreProductAssignFormSection = ({
  title,
  subTitle,
  selectedProducts,
  onChange,
  isSubmitting,
  loading,
  disabled,
  enableQuantityChange,
  hideBundleProducts,
  showPrice
}) => {
  const navigate = useNavigate();
  const { state } = useContext(StoreContext);
  const { state: modalState, onOpenModal, onCloseModal } = useModalState();

  return (
    <EditProductCard
      contentProps={{ style: { padding: 0 } }}
      title={title}
      titleAction={
        !disabled && (
          <AppFlexbox style={{ gap: 8 }}>
            <Button
              color="dark"
              disabled={isSubmitting}
              leftSection={<Plus />}
              onClick={() => onOpenModal('add_products')}
              size="compact-sm"
              type="button"
              variant="filled"
            >
              Add
            </Button>
          </AppFlexbox>
        )
      }
    >
      <AppStack style={{ gap: 0 }}>
        {subTitle && (
          <AppStack style={{ padding: 16, paddingTop: 0 }}>
            {typeof subTitle === 'string' ? (
              <AppText style={{ fontSize: 14, color: '#666' }}>
                {subTitle}
              </AppText>
            ) : (
              subTitle
            )}
          </AppStack>
        )}

        {loading ? (
          <>
            <Divider />
            <AppStack
              style={{
                gap: 5,
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px 10px',
                flex: 1
              }}
            >
              <Loader color="dark" size={34} />
            </AppStack>
          </>
        ) : selectedProducts.length === 0 ? (
          <>
            <Divider />
            <AppStack
              style={{
                padding: 16,
                gap: 10,
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <AppText
                style={{ fontSize: 14, color: '#666', fontStyle: 'italic' }}
              >
                No products added.
              </AppText>
              {!disabled && (
                <Button
                  color="dark"
                  disabled={isSubmitting}
                  leftSection={<Plus />}
                  onClick={() => onOpenModal('add_products')}
                  size="compact-sm"
                  type="button"
                  variant="outline"
                >
                  Add product
                </Button>
              )}
            </AppStack>
          </>
        ) : (
          <SortableList
            items={selectedProducts}
            onChange={(newList) => {
              onChange(
                newList.map((p, index) => ({
                  ...p,
                  sort: index + 1
                }))
              );
            }}
            renderItem={(
              item,
              {
                attributes,
                listeners,
                isDragging,
                setNodeRef,
                style,
                isOverlay
              }
            ) => {
              const product = state.ecomStoreProducts.value.find(
                (p) => p.ecomVendorProduct.uuid === item.vendorProductUuid
              );
              const productStatusInfo = product
                ? getEcomStoreProductStatus(product)
                : null;

              const image =
                item.ecomVendorProduct.previewImage ||
                product?.ecomVendorProduct.ecomVendorBundle?.ecomVendorBundleProducts
                  .sort((a, b) => a.sort - b.sort)
                  .map((bp) => bp.previewImage)
                  .filter(Boolean)[0];

              return (
                <AppCard
                  ref={setNodeRef}
                  radius={0}
                  shadow="none"
                  style={{
                    ...style,
                    padding: 0,
                    outline: isOverlay ? 'solid 1px #dee2e6' : 'none',
                    backgroundColor: isDragging
                      ? 'rgba(56, 56, 56, 0.1)'
                      : '#FFF',
                    opacity: isDragging ? 0.4 : 1,
                    cursor: isDragging ? 'grabbing' : 'default'
                  }}
                >
                  <AppFlexbox
                    style={{
                      padding: 10,
                      gap: 10,
                      borderTop: isOverlay ? 'none' : 'solid 1px #dee2e6',
                      flex: 1,
                      alignItems: 'center'
                    }}
                  >
                    {!disabled && (
                      <ActionIcon
                        color="dark"
                        disabled={isSubmitting}
                        variant="subtle"
                        {...attributes}
                        {...listeners}
                      >
                        <GripVertical size={20} />
                      </ActionIcon>
                    )}

                    <AppFlexbox
                      style={{
                        flex: 1,
                        gap: 10,
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <AppFlexbox style={{ alignItems: 'center' }}>
                        <AppStack
                          style={{
                            width: 40,
                            height: 40,
                            border: !image ? 'solid 1px #dee2e6' : 'none'
                          }}
                        >
                          <Image fit="fill" h={40} src={image} w={40} />
                        </AppStack>
                        <AppStack style={{ fles: 1, gap: 0 }}>
                          <Anchor
                            onClick={() =>
                              navigate(
                                `/merchant/products/${item.ecomVendorProduct.uuid}`
                              )
                            }
                            style={{
                              fontSize: 14,
                              fontWeight: 500,
                              color: '#000'
                            }}
                          >
                            {item.ecomVendorProduct.name}
                          </Anchor>

                          {showPrice && (
                            <AppText style={{ fontSize: 12, color: '#666' }}>
                              {item.ecomVendorProduct.minPrice !==
                              item.ecomVendorProduct.maxPrice
                                ? `${currencyFormat(
                                    item.ecomVendorProduct.minPrice / 100
                                  )} - ${currencyFormat(
                                    item.ecomVendorProduct.maxPrice / 100
                                  )}`
                                : currencyFormat(
                                    item.ecomVendorProduct.minPrice / 100
                                  )}
                            </AppText>
                          )}

                          <AppText style={{ fontSize: 12, color: '#666' }}>
                            {item.ecomVendorProduct.vendorName}
                          </AppText>
                          {productStatusInfo ? (
                            <AppText
                              style={{
                                fontSize: 12,
                                color: productStatusInfo.color
                              }}
                            >
                              {productStatusInfo.label}
                            </AppText>
                          ) : (
                            <AppText
                              style={{
                                fontSize: 12,
                                color: '#C40000'
                              }}
                            >
                              Product not found in store
                            </AppText>
                          )}
                        </AppStack>
                        {item.ecomVendorProduct.ecomVendorBundle && (
                          <Badge color="blue" size="xs">
                            Bundle
                          </Badge>
                        )}
                      </AppFlexbox>

                      <AppFlexbox style={{ alignItems: 'center', gap: 8 }}>
                        {disabled ? (
                          enableQuantityChange && (
                            <AppText style={{ fontSize: 14, color: '#666' }}>
                              Quantity: {item.count}
                            </AppText>
                          )
                        ) : (
                          <>
                            {enableQuantityChange && (
                              <NumberInput
                                disabled={isSubmitting || loading || disabled}
                                min={1}
                                onChange={(v) => {
                                  onChange(
                                    [...selectedProducts].map((p) =>
                                      p.key === item.key
                                        ? { ...p, count: Math.max(v, 1) }
                                        : p
                                    )
                                  );
                                }}
                                placeholder="Quantity"
                                radius={0}
                                size="sm"
                                style={{ maxWidth: 150 }}
                                styles={{
                                  label: {
                                    fontSize: 14,
                                    fontWeight: 500
                                  }
                                }}
                                value={item.count}
                              />
                            )}

                            <ActionableIcon
                              color="red"
                              disabled={isSubmitting}
                              onClick={() =>
                                onChange(
                                  selectedProducts.filter(
                                    (p) => p.key !== item.key
                                  )
                                )
                              }
                              radius="sm"
                              variant="subtle"
                            >
                              <Trash />
                            </ActionableIcon>
                          </>
                        )}
                      </AppFlexbox>
                    </AppFlexbox>
                  </AppFlexbox>
                </AppCard>
              );
            }}
          />
        )}

        <StoreProductAssignModal
          hideBundleProducts={hideBundleProducts}
          isOpen={modalState.isOpen && !disabled}
          onClose={onCloseModal}
          onConfirm={(ecomStoreProducts) => {
            onChange([
              ...selectedProducts,
              ...ecomStoreProducts
                .filter(
                  (p) =>
                    !selectedProducts.find(
                      (sp) => sp.vendorProductUuid === p.ecomVendorProduct.uuid
                    )
                )
                .map((p, index) => ({
                  key: new Date().getTime() + index,
                  vendorProductUuid: p.ecomVendorProduct.uuid,
                  sort: selectedProducts.length + index + 1,
                  count: 1,
                  ecomVendorProduct: {
                    ...p.ecomVendorProduct,
                    minPrice: p.minVendorPrice,
                    maxPrice: p.maxVendorPrice
                  }
                }))
            ]);
            onCloseModal();
          }}
          vendorProductUuids={selectedProducts.map((p) => p.vendorProductUuid)}
        />
      </AppStack>
    </EditProductCard>
  );
};

StoreProductAssignFormSection.propTypes = {
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  enableQuantityChange: PropTypes.bool,
  hideBundleProducts: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  loading: PropTypes.bool,
  selectedProducts: PropTypes.array,
  showPrice: PropTypes.bool,
  subTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  title: PropTypes.string
};

export default StoreProductAssignFormSection;
